import {
  titleUI,
  textUI,
  currentOrPastDateUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import VaDateField from 'platform/forms-system/src/js/web-component-fields/VaDateField';
import VaMemorableDateField from 'platform/forms-system/src/js/web-component-fields/VaMemorableDateField';
import {
  currentYear,
  parseISODate,
} from 'platform/forms-system/src/js/helpers';
import { isValidDate } from 'platform/forms-system/src/js/utilities/validations';
import { applicationInfoFields, FORM_21_4502 } from '../definitions/constants';

const { VETERAN_STATUS_INFORMATION: V } = FORM_21_4502;

const isCompleteValidDate = value => {
  const { day, month, year } = parseISODate(value);

  return (
    [day, month, year].every(Boolean) &&
    Number(year) >= 1900 &&
    Number(year) <= currentYear &&
    isValidDate(day, month, year)
  );
};

const formatDateLong = value => {
  const { day, month, year } = parseISODate(value);

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
  ).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const validateDateOfRelease = (errors, value, formData) => {
  if (!formData?.form214502ServiceDateValidation) {
    return;
  }
  const { day, month, year } = parseISODate(value);
  const hasAnyDatePart = [day, month, year].some(Boolean);
  const hasCompleteDate = [day, month, year].every(Boolean);

  if (!value || !hasAnyDatePart) {
    errors.addError(V.ERROR_DATE_RELEASE_REQUIRED);
    return;
  }

  if (!hasCompleteDate) {
    errors.addError(V.ERROR_DATE_RELEASE_INCOMPLETE);
    return;
  }

  if (
    Number(year) < 1900 ||
    Number(year) > currentYear ||
    !isValidDate(day, month, year)
  ) {
    errors.addError(V.ERROR_DATE_RELEASE_INVALID);
    return;
  }

  const dateOfEntry =
    formData?.[applicationInfoFields.parentObject]?.[
      applicationInfoFields.dateOfEntry
    ];

  if (isCompleteValidDate(dateOfEntry) && value < dateOfEntry) {
    errors.addError(
      V.ERROR_DATE_RELEASE_BEFORE_ENTRY(formatDateLong(dateOfEntry)),
    );
  }
};

const buildVeteranStatusInformationPage = ({
  DateField,
  monthSelect = false,
}) => {
  const dateUI = currentOrPastDateUI({
    title: V.DATE_OF_RELEASE,
    required: () => true,
    errorMessages: {
      pattern: V.ERROR_DATE_RELEASE_INVALID,
      required: V.ERROR_DATE_RELEASE_REQUIRED,
    },
  });

  return {
    uiSchema: {
      'ui:options': {
        preserveHiddenData: true,
        forceDivWrapper: true,
      },
      ...titleUI(V.TITLE),
      [applicationInfoFields.parentObject]: {
        [applicationInfoFields.placeOfRelease]: textUI({
          title: V.PLACE_OF_RELEASE,
          required: () => true,
          errorMessages: {
            required: V.ERROR_PLACE_OF_RELEASE,
          },
        }),
        [applicationInfoFields.dateOfRelease]: {
          ...dateUI,
          'ui:options': {
            ...dateUI['ui:options'],
            ...(monthSelect ? { monthSelect: true } : {}),
          },
          'ui:webComponentField': DateField,
          'ui:errorMessages': {
            pattern: V.ERROR_DATE_RELEASE_INVALID,
            required: V.ERROR_DATE_RELEASE_REQUIRED,
          },
          'ui:validations': [validateDateOfRelease],
        },
      },
    },
    schema: {
      type: 'object',
      properties: {
        [applicationInfoFields.parentObject]: {
          type: 'object',
          properties: {
            [applicationInfoFields.placeOfRelease]: {
              type: 'string',
              maxLength: 100,
            },
            [applicationInfoFields.dateOfRelease]: { type: 'string' },
          },
          required: [
            applicationInfoFields.placeOfRelease,
            applicationInfoFields.dateOfRelease,
          ],
        },
      },
    },
  };
};

export const veteranStatusInformationNewFlow =
  buildVeteranStatusInformationPage({
    DateField: VaMemorableDateField,
    monthSelect: true,
  });

export default buildVeteranStatusInformationPage({
  DateField: VaDateField,
});
